/**
 * Pure helpers behind the order form: pre-filling it from an existing order
 * (to clone it) and building the create request body.
 *
 * @typedef {import("../../../api/types").Order} Order
 * @typedef {import("../components/OrderItemsEditor").ItemRow} ItemRow
 * @typedef {import("../components/OrderProductsEditor").ProductRow} ProductRow
 * @typedef {import("../components/OrderDevicesEditor").DeviceRow} DeviceRow
 * @typedef {import("../components/OrderDiscountEditor").Discount} Discount
 */

const parseFields = (content) => {
    try {
        const parsed = JSON.parse(content || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
};

/**
 * Form state holding the same values as an existing order.
 * @param {Order} order
 */
export const orderToFormState = (order) => ({
    clientId: String(order.client_id),
    observations: order.observations ?? "",
    items: /** @type {ItemRow[]} */ (
        order.order_items.map((item, index) => ({
            key: index + 1,
            serviceItemId: String(item.service_item.id),
            quantity: String(item.quantity),
        }))
    ),
    products: /** @type {ProductRow[]} */ (
        (order.order_products ?? []).map((line, index) => ({
            key: index + 1,
            productId: String(line.product.id),
            quantity: String(line.quantity),
        }))
    ),
    devices: /** @type {DeviceRow[]} */ (
        order.order_devices.map((device, index) => ({
            key: index + 1,
            deviceTypeId: device.device_type ? String(device.device_type.id) : "",
            description: device.description,
            fields: parseFields(device.fields_content),
        }))
    ),
    discount: /** @type {Discount|null} */ (
        order.discount_type && Number(order.discount_value) > 0
            ? {
                  type: order.discount_type,
                  value: String(Number(order.discount_value)),
                  description: order.discount_description ?? "",
              }
            : null
    ),
});

/**
 * The create request body.
 * @param {{
 *   clientId: string,
 *   observations: string,
 *   items: ItemRow[],
 *   products: ProductRow[],
 *   devices: DeviceRow[],
 *   discount: Discount|null,
 * }} state
 */
export const buildOrderPayload = ({clientId, observations, items, products, devices, discount}) => ({
    client_id: Number(clientId),
    observations: observations.trim() || null,
    discount_type: discount?.type ?? null,
    discount_value: discount?.value ?? null,
    discount_description: discount?.description || null,
    order_items: items.map((row) => ({
        service_item_id: Number(row.serviceItemId),
        quantity: Number(row.quantity),
    })),
    order_products: products.map((row) => ({
        product_id: Number(row.productId),
        quantity: Number(row.quantity),
    })),
    order_devices: devices.map((row) => ({
        device_type_id: row.deviceTypeId ? Number(row.deviceTypeId) : null,
        description: row.description.trim(),
        fields_content: row.fields,
    })),
});

/**
 * What a catalog (services or products) offers an order being made or edited:
 * the active entries, and the ones an edited order already has even if they
 * were deactivated since. A line keeps the price it was made with, so that is
 * the price shown for its entry. `ids` are the active entries' ids.
 * @template {{id: number, status: string, base_value: string}} Entry
 * @param {Entry[]} catalog
 * @param {{base_value?: string}[]} lines the order's own lines, if it has any
 * @param {(line: any) => string} entryId the id of the entry a line is for
 * @returns {{entries: Entry[], ids: Set<string>}}
 */
export const catalogForOrder = (catalog, lines, entryId) => {
    const onOrder = new Set(lines.map(entryId));
    const active = catalog.filter((entry) => entry.status === "AC" || onOrder.has(String(entry.id)));
    const frozen = new Map([...lines].reverse().map((line) => [entryId(line), line.base_value]));
    return {
        entries: active.map((entry) =>
            frozen.has(String(entry.id)) ? {...entry, base_value: frozen.get(String(entry.id))} : entry
        ),
        ids: new Set(active.map((entry) => String(entry.id))),
    };
};
